/**
 * Date Utilities for Family Calendar
 *
 * Provides consistent date extraction and formatting functions
 * to avoid timezone issues when comparing dates.
 *
 * @module features/family-calendar/utils/date.utils
 */

import { format } from 'date-fns';

/**
 * Extract local date string (YYYY-MM-DD) from an ISO date string or Date object.
 *
 * This function correctly handles timezone conversion by parsing the date
 * and formatting it in the user's local timezone, avoiding the common bug
 * where `toISOString().split('T')[0]` returns UTC date instead of local date.
 *
 * @param dateInput - ISO date string or Date object
 * @returns Local date string in YYYY-MM-DD format
 *
 * @example
 * ```typescript
 * // Event stored as UTC: 2025-12-14T22:00:00.000Z (which is Dec 15 00:00 in UTC+2)
 * toLocalDateKey('2025-12-14T22:00:00.000Z'); // Returns '2025-12-15' in UTC+2 timezone
 *
 * // Compare with calendar day
 * if (toLocalDateKey(event.startDate) === selectedDate) { ... }
 * ```
 */
export function toLocalDateKey(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return format(date, 'yyyy-MM-dd');
}

/**
 * Get today's date as a local date string (YYYY-MM-DD).
 *
 * @returns Today's local date in YYYY-MM-DD format
 *
 * @example
 * ```typescript
 * const today = getTodayDateKey(); // '2025-12-15'
 * ```
 */
export function getTodayDateKey(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Get a date relative to today as a local date string (YYYY-MM-DD).
 *
 * @param daysFromToday - Number of days from today (positive = future, negative = past)
 * @returns Local date string in YYYY-MM-DD format
 *
 * @example
 * ```typescript
 * getRelativeDateKey(7);  // '2025-12-22' (7 days from now)
 * getRelativeDateKey(-1); // '2025-12-14' (yesterday)
 * ```
 */
export function getRelativeDateKey(daysFromToday: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return format(date, 'yyyy-MM-dd');
}
